// Third Party
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  DefaultOptions,
} from '@apollo/client';
import { useMemo } from 'react';
import merge from 'deepmerge';
import isEqual from 'lodash/isEqual';
import type { AppProps } from 'next/app';

// Utils
import { isSSR } from './predicates';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

type IsomorphicApolloClient = ApolloClient<NormalizedCacheObject>;

let apolloClient: IsomorphicApolloClient | undefined;

function createApolloClient(): IsomorphicApolloClient {
  const link = createHttpLink({
    uri: '/api/graphql/',
  });

  const defaultOptions: DefaultOptions = {
    // watchQuery: {
    //   fetchPolicy: 'no-cache',
    //   errorPolicy: 'ignore',
    // },
    // mutate: {
    //   fetchPolicy: 'no-cache',
    // errorPolicy: 'all',
    // },
  };

  return new ApolloClient({
    ssrMode: isSSR,
    cache: new InMemoryCache(),
    link,
    defaultOptions,
  });
}

export function initializeApollo(
  initialState: any = null
): IsomorphicApolloClient {
  const _apolloClient = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state get hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client
  if (isSSR) return _apolloClient;

  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export const addApolloState = (
  client: ApolloClient<NormalizedCacheObject>,
  pageProps: AppProps['pageProps']
) => {
  // @ts-ignore
  if (pageProps?.props) {
    // @ts-ignore
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
};

export function useApollo(pageProps: AppProps['pageProps']) {
  // @ts-ignore
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo(state), [state]);
  return store;
}
